import { useEffect, useState } from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

/**
 * Determines which teammates and represented experts can be added to a conversation.
 *
 * Fetches participant profiles to find the current user's team, then returns the
 * current user's teammates and the experts represented by anyone on that team.
 * Only the current user's team is considered — the other party's teammates are
 * intentionally excluded.
 *
 * Returns two lists:
 * - `invitableTeammates`: members of the current user's team who are NOT already
 *   in the conversation
 * - `representedExperts`: experts represented by anyone on the current user's
 *   team, NOT already participating. Each expert is enriched with a `profile_id`
 *   (resolved via self-representation) if the expert has their own account.
 *   Experts without a profile_id cannot be added to a conversation.
 *
 * The "Add Contact" button should be shown when either list has entries.
 *
 * @param {Object} conversation - The conversation object from the API
 * @param {Array} conversation.conversation_receivers - Receiver profiles (each has `slug`, `id`)
 * @param {Array} conversation.conversation_senders - Sender profiles (each has `slug`, `id`)
 * @returns {{ invitableTeammates: Array, representedExperts: Array }}
 */
function useConversationTeammates(conversation) {
  const [invitableTeammates, setInvitableTeammates] = useState([]);
  const [representedExperts, setRepresentedExperts] = useState([]);
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const currentReceiverSlug = globalThis.Qwoted.CURRENT_SLUG;
  const currentReceiverProfileId = globalThis.Qwoted.CURRENT_PROFILE_ID;
  // Derive a stable string key from participant slugs so the effect only
  // re-runs when the participant list actually changes, not on object reference changes.
  const participantKey = conversation
    ? [
        ...new Set([
          ...(conversation.conversation_receivers || []).map((p) => p.slug),
          ...(conversation.conversation_senders || []).map((p) => p.slug),
        ]),
      ]
        .sort()
        .join(',')
    : null;

  useEffect(() => {
    if (!participantKey) {
      setInvitableTeammates([]);
      setRepresentedExperts([]);
      setCurrentTeamId(null);
      return undefined;
    }

    const participantSlugs = participantKey.split(',');

    let cancelled = false;

    const fetchData = async () => {
      try {
        // Step 1: Fetch profiles for all participants, then narrow to the current user's team.
        // We only invite teammates from the caller's team, never from the counterparty's.
        const profilesResult = await qapi.userProfiles.getAllBySlugs(participantSlugs);
        if (cancelled) return;

        if (profilesResult.error) {
          qlog.error('Failed to fetch participant profiles:', profilesResult.error);
          return;
        }

        const profiles = Array.isArray(profilesResult.data) ? profilesResult.data : [];
        const currentUserProfile = profiles.find(
          (p) => (currentReceiverProfileId != null && p.profile_id === currentReceiverProfileId)
            || p.slug === currentReceiverSlug,
        );
        const resolvedTeamId = currentUserProfile?.team_id;

        // Step 2: Resolve the "team" set whose represented experts we'll fetch in Step 3.
        // - On a team: actual team members.
        // - Solo PR (no team_id): treat as a "team of one" — the user themselves — so their
        //   own represented experts still surface in Step 3.
        let allTeamMembers;
        if (resolvedTeamId) {
          if (!cancelled) setCurrentTeamId(resolvedTeamId);
          const teamMembersResult = await qapi.userProfiles.getByTeamIds([resolvedTeamId]);
          if (cancelled) return;
          if (teamMembersResult.error) {
            qlog.error('Failed to fetch team members:', teamMembersResult.error);
            return;
          }
          allTeamMembers = Array.isArray(teamMembersResult.data) ? teamMembersResult.data : [];
        } else {
          if (!cancelled) setCurrentTeamId(null);
          allTeamMembers = currentUserProfile ? [currentUserProfile] : [];
        }

        // Filter out anyone already participating → invitable teammates
        const participantSlugSet = new Set(participantSlugs);
        const invitable = allTeamMembers.filter((member) => !participantSlugSet.has(member.slug));
        if (!cancelled) setInvitableTeammates(invitable);

        // Step 3: Fetch experts represented by any team member
        const allTeamMemberProfileIds = allTeamMembers.map((m) => m.profile_id).filter(Boolean);
        if (allTeamMemberProfileIds.length === 0) return;

        const repsResult = await qapi.representations.getExpertsForPromoterProfileIds(allTeamMemberProfileIds);
        if (cancelled) return;

        if (repsResult.error) {
          qlog.error('Failed to fetch represented experts:', repsResult.error);
          return;
        }

        const reps = Array.isArray(repsResult.data) ? repsResult.data : [];

        // Dedupe by promoted_entity_slug and exclude experts already participating
        const seenSlugs = new Set();
        const experts = reps.filter((r) => {
          if (participantSlugSet.has(r.promoted_entity_slug)) return false;
          if (seenSlugs.has(r.promoted_entity_slug)) return false;
          seenSlugs.add(r.promoted_entity_slug);
          return true;
        });

        if (experts.length === 0) {
          setRepresentedExperts([]);
          return;
        }

        // Step 4: Resolve profile_ids for experts via their self-representations.
        // If an expert has self_expert=true, their promoter_profile_id IS their own profile_id.
        const expertEntityIds = experts.map((e) => e.promoted_entity_id);
        const selfRepsResult = await qapi.representations.getSelfRepresentationsByEntityIds(expertEntityIds);
        if (cancelled) return;

        const selfReps = !selfRepsResult.error && Array.isArray(selfRepsResult.data) ? selfRepsResult.data : [];
        const entityIdToProfileId = new Map(selfReps.map((sr) => [sr.promoted_entity_id, sr.promoter_profile_id]));

        const enrichedExperts = experts
          .map((expert) => ({
            ...expert,
            profile_id: entityIdToProfileId.get(expert.promoted_entity_id) || null,
          }))
          // Only experts with their own Qwoted account (a self-representation → profile_id) are addable; drop the rest.
          .filter((expert) => expert.profile_id);

        setRepresentedExperts(enrichedExperts);
      } catch (error) {
        if (!cancelled) {
          qlog.error('Failed to fetch conversation teammates:', error);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [participantKey, currentReceiverProfileId, currentReceiverSlug]);

  return {
    invitableTeammates,
    representedExperts,
    currentTeamId,
    // Picker uses these as the server-side `slug=not.in.(...)` exclude list.
    participantSlugs: participantKey ? participantKey.split(',') : [],
  };
}

export default useConversationTeammates;
